import type {
  EditFieldGovernance,
  VendorProfileDocument,
  VendorProfileFacility,
  VendorProfileVendor,
} from "../types";
import type { EditExtraField, EditTarget } from "./EditDrawer";
import { findDocument } from "../utils/documentLookup";
import { POC_COUNTRY_CODE_FIELD } from "../utils/phone";
import { MSME_DATE_FIELD, MSME_DATE_LABEL, todayISODate } from "../utils/msme";

export interface RowInput {
  label: string;
  fieldName: string;
  value: unknown;
}

export function buildEditTarget(
  row: RowInput,
  facilityId: string,
  facility: VendorProfileFacility,
  vendor: VendorProfileVendor | null | undefined,
  documents: VendorProfileDocument[],
  effectiveGovernance: Record<string, EditFieldGovernance>,
): EditTarget | null {
  if (!effectiveGovernance[row.fieldName]) return null;
  if (row.fieldName === "account_number") {
    return buildBankEditTarget(facilityId, facility, documents, effectiveGovernance);
  }
  if (row.fieldName === "address_billing") {
    return buildAddressEditTarget(facilityId, facility, effectiveGovernance);
  }
  if (row.fieldName === "poc_contact") {
    return buildPocContactEditTarget(facilityId, facility, effectiveGovernance);
  }
  if (row.fieldName === "msme_certificate") {
    return buildMsmeEditTarget(
      facilityId,
      facility,
      vendor,
      documents,
      effectiveGovernance,
    );
  }
  if (row.fieldName === "vendor_type") {
    return buildVendorTypeEditTarget(
      facilityId,
      facility,
      vendor,
      documents,
      effectiveGovernance,
    );
  }
  if (row.fieldName === "super_category") {
    return buildProfileTypeEditTarget(facilityId, facility, effectiveGovernance);
  }
  if (row.fieldName === "pan_number") {
    return buildPanNumberEditTarget(
      facilityId,
      facility,
      vendor,
      documents,
      effectiveGovernance,
    );
  }
  return {
    facilityId,
    fieldName: row.fieldName,
    label: row.label,
    currentValue: row.value,
    governance: effectiveGovernance[row.fieldName]!,
  };
}

function buildBankEditTarget(
  facilityId: string,
  facility: VendorProfileFacility,
  documents: VendorProfileDocument[],
  effectiveGovernance: Record<string, EditFieldGovernance>,
): EditTarget {
  const bankVerificationDoc = findDocument(
    documents,
    "facility",
    facility.id,
    "Bank_Verification",
  );
  const confirm: EditExtraField = {
    fieldName: "__confirm_account_number",
    label: "Re-enter Account Number",
    currentValue: "",
    type: "confirm",
    matches: "__primary__",
  };
  const bankName: EditExtraField = {
    fieldName: "bank_name",
    label: "Bank Name",
    currentValue: facility.bank_name ?? "",
    type: "readonly",
  };
  const branchName: EditExtraField = {
    fieldName: "branch_name",
    label: "Bank Branch",
    currentValue: facility.branch_name ?? "",
    type: "readonly",
  };
  const accountName: EditExtraField = {
    fieldName: "account_name",
    label: "Account Holder Name",
    currentValue: facility.account_name ?? "",
    placeholder: "As on bank records",
  };
  const accountType: EditExtraField = {
    fieldName: "account_type",
    label: "Account Type",
    currentValue: facility.account_type ?? "",
    placeholder: "Current / Savings / Overdraft",
  };
  const cancelledCheque: EditExtraField = {
    fieldName: "bank_verification_document",
    label: "Cancelled Cheque",
    currentValue: bankVerificationDoc?.file ?? "",
    type: "document",
    currentAttachment: bankVerificationDoc,
  };
  return {
    facilityId,
    fieldName: "account_number",
    label: "Bank Account Number",
    currentValue: facility.account_number ?? "",
    governance: effectiveGovernance["account_number"]!,
    extraFields: [
      confirm,
      {
        fieldName: "ifsc_code",
        label: "IFSC Code",
        currentValue: facility.ifsc_code ?? "",
        placeholder: "HDFC0001234",
      },
      bankName,
      branchName,
      accountName,
      accountType,
      cancelledCheque,
    ],
  };
}

function buildPocContactEditTarget(
  facilityId: string,
  facility: VendorProfileFacility,
  effectiveGovernance: Record<string, EditFieldGovernance>,
): EditTarget {
  return {
    facilityId,
    fieldName: "poc_contact",
    label: "POC Number",
    currentValue: facility.poc_contact,
    pocCountryCode: facility.poc_contact_country_code ?? null,
    countryCodeField: effectiveGovernance[POC_COUNTRY_CODE_FIELD]
      ? POC_COUNTRY_CODE_FIELD
      : undefined,
    governance: effectiveGovernance["poc_contact"]!,
  };
}

function buildAddressEditTarget(
  facilityId: string,
  facility: VendorProfileFacility,
  effectiveGovernance: Record<string, EditFieldGovernance>,
): EditTarget {
  return {
    facilityId,
    fieldName: "address_billing",
    label: "Billing Address",
    currentValue: facility.address_billing,
    governance: effectiveGovernance["address_billing"]!,
    extraFields: [
      {
        fieldName: "location_pincode",
        label: "Pincode",
        currentValue: facility.location_pincode,
        placeholder: "560058",
      },
      {
        fieldName: "billing_city",
        label: "City",
        currentValue: facility.billing_city ?? "",
        type: "readonly",
      },
      {
        fieldName: "billing_country",
        label: "Country",
        currentValue: facility.billing_country ?? "",
        type: "readonly",
      },
    ],
  };
}

function buildMsmeEditTarget(
  facilityId: string,
  _facility: VendorProfileFacility,
  vendor: VendorProfileVendor | null | undefined,
  documents: VendorProfileDocument[],
  effectiveGovernance: Record<string, EditFieldGovernance>,
): EditTarget {
  const msmeDoc = vendor
    ? findDocument(documents, "vendor", vendor.id, "MRC")
    : undefined;
  return {
    facilityId,
    fieldName: "msme_certificate",
    label: "MSME Certificate",
    currentValue: msmeDoc?.file ?? "",
    governance: effectiveGovernance["msme_certificate"]!,
    kind: "document",
    currentAttachmentName: msmeDoc?.file ?? null,
    currentAttachment: msmeDoc,
    extraFields: [
      {
        fieldName: "msme_number",
        label: "MSME / Udyam Number",
        currentValue: vendor?.msme_number ?? "",
        placeholder: "UDYAM-KR-01-0056789",
      },
      {
        fieldName: MSME_DATE_FIELD,
        label: MSME_DATE_LABEL,
        currentValue: vendor?.msme_registration_date ?? "",
        type: "date",
        maxDate: todayISODate(),
      },
    ],
  };
}

function buildPanNumberEditTarget(
  facilityId: string,
  facility: VendorProfileFacility,
  vendor: VendorProfileVendor | null | undefined,
  documents: VendorProfileDocument[],
  effectiveGovernance: Record<string, EditFieldGovernance>,
): EditTarget {
  void documents;
  return {
    facilityId,
    fieldName: "pan_number",
    label: "PAN Number",
    currentValue: vendor?.pan_card ?? "",
    governance: effectiveGovernance["pan_number"]!,
    extraFields: [
      {
        fieldName: "legal_name",
        label: "Vendor Legal Name",
        currentValue: facility.legal_name ?? "",
        type: "readonly",
        required: true,
        placeholder: "Auto-filled from PAN",
      },
      {
        fieldName: "pan_card_attachment",
        label: "PAN Card",
        currentValue: "",
        type: "document",
      },
    ],
  };
}

function buildVendorTypeEditTarget(
  facilityId: string,
  facility: VendorProfileFacility,
  vendor: VendorProfileVendor | null | undefined,
  documents: VendorProfileDocument[],
  effectiveGovernance: Record<string, EditFieldGovernance>,
): EditTarget {
  const gstCertDoc = findDocument(
    documents,
    "facility",
    facility.id,
    "Facility_GST_Certificate",
  );
  return {
    facilityId,
    fieldName: "vendor_type",
    label: "Vendor Type",
    currentValue: vendor?.type ?? "",
    governance: effectiveGovernance["vendor_type"]!,
    gstAddressAutofill: facility.is_primary,
    extraFields: [
      {
        fieldName: "gst",
        label: "GST Number",
        currentValue: facility.gst ?? "",
        placeholder: "29AAGCM1234K1Z5",
      },
      {
        fieldName: "billing_state",
        label: "Billing State",
        currentValue: facility.billing_state ?? "",
        type: "readonly",
        placeholder: "Auto-filled from GST",
        required: true,
      },
      {
        fieldName: "address_billing",
        label: "Billing Address",
        currentValue: facility.address_billing,
      },
      {
        fieldName: "location_pincode",
        label: "Pincode",
        currentValue: facility.location_pincode,
        placeholder: "560058",
      },
      {
        fieldName: "billing_city",
        label: "City",
        currentValue: facility.billing_city ?? "",
        type: "readonly",
      },
      {
        fieldName: "billing_country",
        label: "Country",
        currentValue: facility.billing_country ?? "",
        type: "readonly",
      },
      {
        fieldName: "gst_certificate",
        label: "GST Certificate",
        currentValue: gstCertDoc?.file ?? "",
        type: "document",
        currentAttachment: gstCertDoc,
      },
    ],
  };
}

function buildProfileTypeEditTarget(
  facilityId: string,
  facility: VendorProfileFacility,
  effectiveGovernance: Record<string, EditFieldGovernance>,
): EditTarget {
  return {
    facilityId,
    fieldName: "super_category",
    label: "Super Category",
    currentValue: facility.super_category ?? "",
    governance: effectiveGovernance["super_category"]!,
    extraFields: [
      {
        fieldName: "sub_category",
        label: "Sub Category",
        currentValue: facility.sub_category ?? "",
        type: "sub_category",
        required: true,
      },
    ],
  };
}
